/** Helpers for replacing an IFC on the same house+discipline without losing XYZ. */

export const DEFAULT_IFC_TRANSFORM = {
  x: 0,
  y: 0,
  z: 0,
  rotX: 0,
  rotY: 0,
  rotZ: 0,
};

const axisValue = (transform, key) => {
  const raw = transform?.[key];
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function normalizeIfcTransform(transform) {
  return {
    x: axisValue(transform, "x"),
    y: axisValue(transform, "y"),
    z: axisValue(transform, "z"),
    rotX: axisValue(transform, "rotX"),
    rotY: axisValue(transform, "rotY"),
    rotZ: axisValue(transform, "rotZ"),
  };
}

/** Non-zero XYZ (or saved yaw) means rematch must leave the model alone. */
export function hasSavedPlacement(transform) {
  const next = normalizeIfcTransform(transform);
  return next.x !== 0 || next.y !== 0 || next.z !== 0 || next.rotZ !== 0;
}

export function sameIfcPlacement(a, b) {
  const left = normalizeIfcTransform(a);
  const right = normalizeIfcTransform(b);
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.z === right.z &&
    left.rotX === right.rotX &&
    left.rotY === right.rotY &&
    left.rotZ === right.rotZ
  );
}

const houseKey = (houseId) =>
  houseId == null || houseId === "" ? "" : String(houseId);

export function sameHouseAndDiscipline(model, houseId, discipline) {
  if (!model) return false;
  return (
    houseKey(model.drawing_house_id) === houseKey(houseId) &&
    String(model.discipline || "") === String(discipline || "")
  );
}

export function findExistingIfcModels(models, houseId, discipline) {
  return (models || []).filter((model) =>
    sameHouseAndDiscipline(model, houseId, discipline),
  );
}

export function findExistingIfcModel(models, houseId, discipline) {
  return findExistingIfcModels(models, houseId, discipline)[0] || null;
}

/** Prefer a row with saved XYZ/yaw when several occupy the same slot. */
export function pickPlacementSource(models) {
  const rows = models || [];
  return rows.find((model) => hasSavedPlacement(model?.transform)) || rows[0] || null;
}

export function leftoverModelsToDelete(previousModels, keepId) {
  return (previousModels || []).filter(
    (model) => model?.id && String(model.id) !== String(keepId || ""),
  );
}

export function existingFromDuplicateConflict(err) {
  if (err?.response?.status !== 409) return null;
  if (err?.response?.data?.error !== "duplicate_model") return null;
  return err.response.data?.existing_model || null;
}

/** Prefer the in-memory row (this-session nudge) over the 409 snapshot. */
export function resolveExistingIfcModel(localExisting, conflictExisting) {
  if (localExisting && conflictExisting) {
    const localT = normalizeIfcTransform(localExisting.transform);
    const remoteT = normalizeIfcTransform(conflictExisting.transform);
    return {
      ...conflictExisting,
      ...localExisting,
      id: localExisting.id || conflictExisting.id,
      transform: hasSavedPlacement(localT) ? localT : remoteT,
    };
  }
  return localExisting || conflictExisting || null;
}

export function transformToReuse(existing) {
  if (!existing) return null;
  return normalizeIfcTransform(existing.transform);
}
